/**
 * Leviathan Web - Customer Form Component
 * Quasar Prime LLC
 * Reusable form for creating and editing customers
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

interface CustomerFormData {
  name: string;
  displayName: string;
  customerNumber: string;
  type: 'COMMERCIAL' | 'RESIDENTIAL' | 'GOVERNMENT' | 'INDUSTRIAL';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'LEAD';
  primaryContactFirstName: string;
  primaryContactLastName: string;
  email: string;
  phone: string;
  fax: string;
  website: string;
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  taxId: string;
  industryType: string;
  creditLimit: string;
  creditTerms: string;
  pricingTier: string;
  paymentMethod: string;
  notes: string;
}

interface CustomerFormProps {
  customerId?: string;
  initialData?: Partial<CustomerFormData>;
  onSuccess?: () => void;
}

export default function CustomerForm({ customerId, initialData, onSuccess }: CustomerFormProps) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sameAsBilling, setSameAsBilling] = useState(false);

  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    displayName: '',
    customerNumber: '',
    type: 'COMMERCIAL',
    status: 'LEAD',
    primaryContactFirstName: '',
    primaryContactLastName: '',
    email: '',
    phone: '',
    fax: '',
    website: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZip: '',
    taxId: '',
    industryType: '',
    creditLimit: '',
    creditTerms: '30',
    pricingTier: 'STANDARD',
    paymentMethod: 'INVOICE',
    notes: '',
    ...initialData,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If billing address changes and sameAsBilling is checked, update delivery
    if (sameAsBilling && name.startsWith('billing')) {
      const deliveryField = name.replace('billing', 'delivery');
      setFormData((prev) => ({ ...prev, [deliveryField]: value }));
    }
  };

  const handleSameAsBilling = (checked: boolean) => {
    setSameAsBilling(checked);
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        deliveryAddress: prev.billingAddress,
        deliveryCity: prev.billingCity,
        deliveryState: prev.billingState,
        deliveryZip: prev.billingZip,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Build payload, converting string numbers to actual numbers
      const payload: Record<string, unknown> = {
        ...formData,
        creditLimit: formData.creditLimit ? parseFloat(formData.creditLimit) : null,
        creditTerms: formData.creditTerms ? parseInt(formData.creditTerms) : null,
      };

      // Remove empty strings, convert to null
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      const url = customerId
        ? `${API_URL}/api/customers/${customerId}`
        : `${API_URL}/api/customers`;

      const method = customerId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/customers');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save customer');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Basic Information */}
      <FormSection title="Basic Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Company Name" required>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="form-input"
            />
          </FormField>

          <FormField label="Display Name">
            <input
              type="text"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Optional short name"
              className="form-input"
            />
          </FormField>

          <FormField label="Customer Number">
            <input
              type="text"
              name="customerNumber"
              value={formData.customerNumber}
              onChange={handleChange}
              placeholder="Auto-generated if empty"
              className="form-input"
            />
          </FormField>

          <FormField label="Type" required>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="COMMERCIAL">Commercial</option>
              <option value="RESIDENTIAL">Residential</option>
              <option value="GOVERNMENT">Government</option>
              <option value="INDUSTRIAL">Industrial</option>
            </select>
          </FormField>

          <FormField label="Status" required>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="LEAD">Lead</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </FormField>

          <FormField label="Industry Type">
            <input
              type="text"
              name="industryType"
              value={formData.industryType}
              onChange={handleChange}
              placeholder="e.g. Construction, Agriculture"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Contact Information */}
      <FormSection title="Contact Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/*
            Two fields, not one.

            Reports sort by surname, and a single "Contact Name" cannot support
            that: "Fred Van Der Berg" and "Dr. Fred Johns Jr." do not split by
            any rule that works every time. The only place the answer is known
            is here, at entry.

            Last name is the required half when a name is given at all — a
            contact known only as "Purchasing" is a real business record, so the
            first name may be left empty and the last may not.
          */}
          <FormField label="Contact First Name">
            <input
              type="text"
              name="primaryContactFirstName"
              value={formData.primaryContactFirstName}
              onChange={handleChange}
              placeholder="First name"
              className="form-input"
            />
          </FormField>

          <FormField label="Contact Last Name">
            <input
              type="text"
              name="primaryContactLastName"
              value={formData.primaryContactLastName}
              onChange={handleChange}
              placeholder="Last name"
              className="form-input"
            />
            {formData.primaryContactFirstName.trim() &&
              !formData.primaryContactLastName.trim() && (
                <p className="text-amber text-xs font-mono mt-1">
                  &gt; Last name is required once a first name is given — lists sort by
                  it. A contact known by one name only goes here.
                </p>
              )}
          </FormField>

          <FormField label="Email">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@example.com"
              className="form-input"
            />
          </FormField>

          <FormField label="Phone">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              className="form-input"
            />
          </FormField>

          <FormField label="Fax">
            <input
              type="tel"
              name="fax"
              value={formData.fax}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              className="form-input"
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Website">
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
                className="form-input"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Billing Address */}
      <FormSection title="Billing Address">
        <div className="grid grid-cols-1 gap-6">
          <FormField label="Street Address">
            <input
              type="text"
              name="billingAddress"
              value={formData.billingAddress}
              onChange={handleChange}
              placeholder="123 Main Street"
              className="form-input"
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField label="City">
              <input
                type="text"
                name="billingCity"
                value={formData.billingCity}
                onChange={handleChange}
                placeholder="City"
                className="form-input"
              />
            </FormField>

            <FormField label="State">
              <input
                type="text"
                name="billingState"
                value={formData.billingState}
                onChange={handleChange}
                maxLength={2}
                placeholder="TX"
                className="form-input uppercase"
              />
            </FormField>

            <FormField label="ZIP Code">
              <input
                type="text"
                name="billingZip"
                value={formData.billingZip}
                onChange={handleChange}
                placeholder="12345"
                className="form-input"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Delivery Address */}
      <FormSection
        title="Delivery Address"
        headerRight={
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sameAsBilling}
              onChange={(e) => handleSameAsBilling(e.target.checked)}
              className="w-4 h-4 text-amber bg-background border-panel-border focus:ring-amber rounded"
            />
            <span className="text-text-secondary text-xs uppercase tracking-widest font-mono">
              Same as Billing
            </span>
          </label>
        }
      >
        <div className="grid grid-cols-1 gap-6">
          <FormField label="Street Address">
            <input
              type="text"
              name="deliveryAddress"
              value={formData.deliveryAddress}
              onChange={handleChange}
              disabled={sameAsBilling}
              placeholder="123 Main Street"
              className="form-input disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField label="City">
              <input
                type="text"
                name="deliveryCity"
                value={formData.deliveryCity}
                onChange={handleChange}
                disabled={sameAsBilling}
                placeholder="City"
                className="form-input disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </FormField>

            <FormField label="State">
              <input
                type="text"
                name="deliveryState"
                value={formData.deliveryState}
                onChange={handleChange}
                disabled={sameAsBilling}
                maxLength={2}
                placeholder="TX"
                className="form-input uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </FormField>

            <FormField label="ZIP Code">
              <input
                type="text"
                name="deliveryZip"
                value={formData.deliveryZip}
                onChange={handleChange}
                disabled={sameAsBilling}
                placeholder="12345"
                className="form-input disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Business & Credit */}
      <FormSection title="Business & Credit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Tax ID / EIN">
            <input
              type="text"
              name="taxId"
              value={formData.taxId}
              onChange={handleChange}
              placeholder="XX-XXXXXXX"
              className="form-input"
            />
          </FormField>

          <FormField label="Credit Limit ($)">
            <input
              type="number"
              name="creditLimit"
              value={formData.creditLimit}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className="form-input"
            />
          </FormField>

          <FormField label="Credit Terms (Days)">
            <select
              name="creditTerms"
              value={formData.creditTerms}
              onChange={handleChange}
              className="form-input"
            >
              <option value="0">Due on Receipt (COD)</option>
              <option value="15">Net 15</option>
              <option value="30">Net 30</option>
              <option value="45">Net 45</option>
              <option value="60">Net 60</option>
              <option value="90">Net 90</option>
            </select>
          </FormField>

          <FormField label="Pricing Tier">
            <select
              name="pricingTier"
              value={formData.pricingTier}
              onChange={handleChange}
              className="form-input"
            >
              <option value="STANDARD">Standard</option>
              <option value="PREMIUM">Premium</option>
              <option value="WHOLESALE">Wholesale</option>
              <option value="GOVERNMENT">Government</option>
            </select>
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Payment Method">
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="form-input"
              >
                <option value="INVOICE">Invoice</option>
                <option value="COD">Cash on Delivery</option>
                <option value="CREDIT_CARD">Credit Card</option>
                <option value="ACH">ACH Transfer</option>
                <option value="CHECK">Check</option>
              </select>
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes">
        <FormField label="Internal Notes">
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Internal notes about this customer..."
            className="form-input resize-none"
          />
        </FormField>
      </FormSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/customers')}
          className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
          style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
        >
          <span className="relative z-10">
            {isLoading ? 'Saving...' : customerId ? 'Update Customer' : 'Create Customer'}
          </span>
          <div
            className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ mixBlendMode: 'overlay' }}
          />
        </button>
      </div>
    </form>
  );
}

/**
 * Form section with geometric panel styling
 */
function FormSection({
  title,
  headerRight,
  children,
}: {
  title: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
            <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
              {title}
            </h3>
          </div>
          {headerRight}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Form field with label
 */
function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
